export type CamDragDropEvent = {
  element: HTMLElement;
  pointerEvent: PointerEvent;
  source: HTMLElement;
  target: HTMLElement;
};

export type CamDragHelperConfig = {
  dragRootElement?: string | HTMLElement | null;
  dropTargetSelector: string;
  onDragStart?: (source: HTMLElement) => void;
  onDrop?: (event: CamDragDropEvent) => void;
  targetSelector: string;
};

const DRAG_THRESHOLD = 4;

export class CamDragHelper {
  private _config: CamDragHelperConfig;
  private _root: HTMLElement | Document;
  private _clone: HTMLElement | null = null;
  private _source: HTMLElement | null = null;
  private _origin: { x: number; y: number } | null = null;
  private _started = false;

  constructor(config: CamDragHelperConfig) {
    this._config = config;
    this._root = this._resolveRoot(config.dragRootElement);
    this._root.addEventListener('pointerdown', this._onPointerDown as EventListener);
  }

  public destroy(): void {
    this._root.removeEventListener('pointerdown', this._onPointerDown as EventListener);
    this._stopTracking();
    this._removeClone();
  }

  private _resolveRoot(root: string | HTMLElement | null | undefined): HTMLElement | Document {
    if (!root) {
      return document;
    }
    if (typeof root !== 'string') {
      return root;
    }
    return document.querySelector<HTMLElement>(root) ?? document;
  }

  private _onPointerDown = (event: PointerEvent) => {
    const target = event.target as HTMLElement | null;
    const source = target?.closest<HTMLElement>(this._config.targetSelector) ?? null;
    if (!source || event.button !== 0) {
      return;
    }
    this._source = source;
    this._origin = { x: event.clientX, y: event.clientY };
    this._started = false;
    document.addEventListener('pointermove', this._onPointerMove);
    document.addEventListener('pointerup', this._onPointerUp);
  };

  private _onPointerMove = (event: PointerEvent) => {
    if (!this._source || !this._origin) {
      return;
    }
    const deltaX = event.clientX - this._origin.x;
    const deltaY = event.clientY - this._origin.y;

    if (!this._started) {
      if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) {
        return;
      }
      this._started = true;
      this._createClone();
      this._config.onDragStart?.(this._source);
    }

    if (this._clone) {
      this._clone.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    }
    event.preventDefault();
  };

  private _onPointerUp = (event: PointerEvent) => {
    const source = this._source;
    const clone = this._clone;
    const started = this._started;

    this._stopTracking();

    if (started && source && clone) {
      const dropTarget = document
        .elementsFromPoint(event.clientX, event.clientY)
        .map(element => (element as HTMLElement).closest<HTMLElement>(this._config.dropTargetSelector))
        .find(Boolean);

      if (dropTarget) {
        this._config.onDrop?.({ element: clone, pointerEvent: event, source, target: dropTarget });
      }
    }

    this._removeClone();
  };

  private _createClone(): void {
    if (!this._source) {
      return;
    }
    const rect = this._source.getBoundingClientRect();
    const clone = this._source.cloneNode(true) as HTMLElement;
    clone.classList.add('cam-drag-proxy');
    clone.style.position = 'fixed';
    clone.style.left = `${rect.left}px`;
    clone.style.top = `${rect.top}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.margin = '0';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '0.8';
    clone.style.zIndex = '10000';
    document.body.appendChild(clone);
    this._clone = clone;
  }

  private _stopTracking(): void {
    document.removeEventListener('pointermove', this._onPointerMove);
    document.removeEventListener('pointerup', this._onPointerUp);
    this._source = null;
    this._origin = null;
    this._started = false;
  }

  private _removeClone(): void {
    this._clone?.remove();
    this._clone = null;
  }
}
